import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { FuelPageSubtitle, FuelPageTitle } from 'pages/FuelManagement/FuelManagement.styled'
import { formatGhs } from 'pages/FuelManagement/fuelOpsDemoData'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { formatNumbersForDisplay } from 'utils/helpers'
import { IFuelMachine } from 'types/fuelConsumption.interface'
import {
  avgProvenanceOf,
  bandDeltaPct,
  bandLabelOf,
  bandStateOf,
  FUEL_CONSUMPTION_MACHINES,
  FUEL_LEAGUE_FOOTNOTE,
  FUEL_LEAGUE_SUBLINE,
  machineClassText,
  machineFuelCost30dGhs,
  machineHeadlineRateLph,
  signedPctLabel,
  worstFuelMachines,
} from './fuelConsumptionDemoData'
import { BurnSparkline } from './BurnSparkline'
import { ModePill, ProvenanceBadge } from './ProvenanceBadge'
import { LeagueFootnote, WorstCard, WorstName, WorstRate, WorstStrip, WorstSub } from './FuelMachineLeague.styled'

const BAND_COLOR = {
  over: tokens.status.danger.fg,
  in: tokens.status.success.fg,
  under: tokens.text.muted,
}

export const FuelMachineLeague = () => {
  const navigate = useNavigate()
  const [classFilter, setClassFilter] = useState<'ALL' | 'MOBILE' | 'STATIC'>('ALL')

  const machines = FUEL_CONSUMPTION_MACHINES
  const worst = worstFuelMachines(machines)
  const filtered = machines.filter((m) =>
    classFilter === 'ALL'
      ? true
      : classFilter === 'MOBILE'
        ? m.machineClass === 'OPERATED_MOBILE'
        : m.machineClass === 'STATIC_PLANT',
  )

  const columns: IHonestTableColumn<IFuelMachine>[] = [
    {
      id: 'machine',
      header: 'MACHINE',
      sticky: true,
      minWidth: '14rem',
      cell: (item) => (
        <Flex column gap='0.25rem'>
          <Flex center gap='0.5rem'>
            <BodyText weight='bold'>{item.name}</BodyText>
            <ModePill mode={item.fuelDataMode} />
          </Flex>
          <OtherText grey>{`${machineClassText(item)} · ${item.model}`}</OtherText>
        </Flex>
      ),
    },
    {
      id: 'litres30d',
      header: '30D LITRES',
      align: 'right',
      cell: (item) => <BodyText weight='bold'>{`${formatNumbersForDisplay(item.litres30d)} L`}</BodyText>,
    },
    {
      id: 'avgRate',
      header: 'AVG L/H',
      minWidth: '11rem',
      cell: (item) => (
        <Flex center gap='0.5rem'>
          <BodyText weight='bold'>{item.avgRateLph}</BodyText>
          <ProvenanceBadge provenance={avgProvenanceOf(item)} />
        </Flex>
      ),
    },
    {
      id: 'vsNominal',
      header: 'VS NOMINAL',
      align: 'right',
      cell: (item) => {
        const delta = bandDeltaPct(item.avgRateLph, item.nominalBurnRateLph)
        const state = bandStateOf(item.avgRateLph, item.nominalBurnRateLph)
        return (
          <BodyText weight='bold' color={BAND_COLOR[state]}>
            {signedPctLabel(delta)}
          </BodyText>
        )
      },
    },
    {
      id: 'litresPerTonne',
      header: 'L/TONNE',
      align: 'right',
      cell: (item) =>
        item.litresPerTonne != null ? (
          <BodyText>{`${item.litresPerTonne} L/t`}</BodyText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'cost30d',
      header: '30D FUEL COST',
      align: 'right',
      cell: (item) => <BodyText>{formatGhs(machineFuelCost30dGhs(item))}</BodyText>,
    },
    {
      id: 'trend',
      header: '7D TREND',
      minWidth: '6rem',
      cell: (item) => {
        const last = item.trend7dLph[item.trend7dLph.length - 1] ?? item.avgRateLph
        return <BurnSparkline values={item.trend7dLph} color={BAND_COLOR[bandStateOf(last, item.nominalBurnRateLph)]} />
      },
    },
    {
      id: 'flags',
      header: 'FLAGS',
      minWidth: '14rem',
      cell: (item) =>
        item.flags.length ? (
          <Flex center gap='0.5rem' wrap>
            {item.flags.map((f) => (
              <StatusPill key={f.kind} status={f.tone} dot={f.tone === 'danger'}>
                {f.label}
              </StatusPill>
            ))}
          </Flex>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex center gap='0.75rem'>
          <FuelPageTitle>Machine fuel</FuelPageTitle>
          <DemoBadge />
        </Flex>
        <FuelPageSubtitle>{FUEL_LEAGUE_SUBLINE}</FuelPageSubtitle>
        {!machines.length ? (
          <NoData text='No machine fuel analytics yet — dispenses and telemetry will build the league here.' />
        ) : (
          <>
            <WorstStrip>
              {worst.map((m) => {
                const headline = machineHeadlineRateLph(m)
                const delta = bandDeltaPct(headline, m.nominalBurnRateLph)
                const state = bandStateOf(headline, m.nominalBurnRateLph)
                const topFlag = m.flags[0]
                return (
                  <WorstCard key={m.id} onClick={() => navigate(`/fuel/machines/${m.id}`)}>
                    <Flex between center gap='0.5rem'>
                      <WorstName>{m.name}</WorstName>
                      {topFlag ? (
                        <StatusPill status={topFlag.tone} dot={topFlag.tone === 'danger'}>
                          {topFlag.label}
                        </StatusPill>
                      ) : (
                        <StatusPill status='success'>IN BAND</StatusPill>
                      )}
                    </Flex>
                    <WorstRate $color={BAND_COLOR[state]}>{`${headline} L/h`}</WorstRate>
                    <WorstSub>
                      {`${signedPctLabel(delta)} ${bandLabelOf(headline, m.nominalBurnRateLph)} · nominal ${m.nominalBurnRateLph}`}
                    </WorstSub>
                  </WorstCard>
                )
              })}
            </WorstStrip>
            <Flex mb='16'>
              <TabsContainer>
                <Tabs active={classFilter === 'ALL'} onClick={() => setClassFilter('ALL')}>
                  All
                </Tabs>
                <Tabs active={classFilter === 'MOBILE'} onClick={() => setClassFilter('MOBILE')}>
                  Mobile
                </Tabs>
                <Tabs active={classFilter === 'STATIC'} onClick={() => setClassFilter('STATIC')}>
                  Static plant
                </Tabs>
              </TabsContainer>
            </Flex>
            <HonestTable
              columns={columns}
              data={filtered}
              keyField='id'
              emptyMessage='No machines in this class yet.'
              onClick={(item) => navigate(`/fuel/machines/${item.id}`)}
            />
            <LeagueFootnote>
              <OtherText color={tokens.text.muted}>{FUEL_LEAGUE_FOOTNOTE}</OtherText>
            </LeagueFootnote>
          </>
        )}
      </PageWrapper>
    </>
  )
}
